import React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  EditorGroup,
  PageEditorShell,
  SegmentedChoiceSetting,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";
import WebPushPromptStyleEditor from "./WebPushPromptStyleEditor";
import { WEB_PUSH_PROMPT_STYLE_DEFAULTS } from "../webPushPromptVisualOwnership";

const DEFAULT_COPY = Object.freeze({
  enabled: true,
  title: "Recibí novedades",
  subscribedTitle: "Notificaciones activadas",
  description: "Activá los avisos de {businessName} para enterarte de novedades y promociones.",
  subscribedDescription: "Este navegador recibe avisos de {businessName}.",
  enableLabel: "Activar notificaciones",
  disableLabel: "Desactivar",
  dismissLabel: "Ahora no",
  processingLabel: "Procesando…",
  successMessage: "Notificaciones activadas correctamente.",
  disableSuccessMessage: "Notificaciones desactivadas.",
  errorMessage: "No se pudieron activar las notificaciones.",
  disableErrorMessage: "No se pudieron desactivar las notificaciones.",
  position: "bottom-right",
});

const PREVIEW_STATES = [
  { value: "initial", label: "Inicial" },
  { value: "subscribed", label: "Suscripto" },
  { value: "processing", label: "Procesando" },
  { value: "success", label: "Éxito" },
  { value: "error", label: "Error" },
];

function promptValue(form) {
  return form?.notifications?.webPushPrompt || {};
}
function value(prompt, key) {
  return prompt?.[key] ?? DEFAULT_COPY[key];
}
function previewText(raw, businessName) {
  return String(raw ?? "").replace(/\{businessName\}/g, businessName);
}
function actionSx(style, processingStyle, busy = false) {
  const source = busy ? { ...style, ...processingStyle } : style;
  return {
    bgcolor: source.backgroundColor,
    color: source.textColor,
    border: `1px solid ${source.borderColor}`,
    boxShadow: style.boxShadow || "none",
    borderRadius: `${style.borderRadius}px`,
    "&:hover": {
      bgcolor: busy ? source.backgroundColor : style.hoverBackgroundColor,
      color: busy ? source.textColor : style.hoverTextColor,
      borderColor: busy ? source.borderColor : style.hoverBorderColor,
    },
    "&.Mui-focusVisible": { outline: `3px solid ${style.focusRingColor}`, outlineOffset: 2 },
    "&.Mui-disabled": {
      bgcolor: source.backgroundColor,
      color: source.textColor,
      borderColor: source.borderColor,
      opacity: 1,
    },
  };
}

function FeedbackPreview({ kind, style, text }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.25,
        p: 1.1,
        bgcolor: style.backgroundColor,
        color: style.textColor,
        borderColor: style.borderColor,
        borderRadius: `${style.borderRadius}px`,
        boxShadow: style.boxShadow || "none",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        <Box component="span" sx={{ color: style.iconColor, mr: 0.75 }}>{kind === "error" ? "●" : "✓"}</Box>
        {text}
      </Typography>
    </Paper>
  );
}

function PromptCard({ prompt, businessName, previewState }) {
  const style = prompt.style || WEB_PUSH_PROMPT_STYLE_DEFAULTS;
  const subscribed = previewState === "subscribed";
  const busy = previewState === "processing";
  const mainAction = subscribed ? style.disableButton : style.enableButton;
  const title = subscribed ? value(prompt, "subscribedTitle") : value(prompt, "title");
  const description = subscribed ? value(prompt, "subscribedDescription") : value(prompt, "description");
  const titleStyle = subscribed ? style.subscribedTitle : style.title;
  const descriptionStyle = subscribed ? style.subscribedDescription : style.description;
  return (
    <Paper elevation={0} sx={{ width: 390, maxWidth: "calc(100% - 24px)", p: 2, bgcolor: style.surface.backgroundColor, border: `${style.surface.borderWidth}px solid ${style.surface.borderColor}`, borderRadius: `${style.surface.borderRadius}px`, boxShadow: style.surface.boxShadow }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ width: 42, height: 42, borderRadius: `${style.icon.borderRadius}px`, border: `${style.icon.borderWidth}px solid ${style.icon.borderColor}`, display: "grid", placeItems: "center", bgcolor: style.icon.surfaceBackgroundColor, color: style.icon.color, flexShrink: 0 }}>
          <NotificationsActiveRoundedIcon sx={{ color: "inherit" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, color: titleStyle.color }}>{previewText(title, businessName)}</Typography>
          <Typography variant="body2" sx={{ mt: 0.35, color: descriptionStyle.color }}>{previewText(description, businessName)}</Typography>
          {previewState === "success" ? <FeedbackPreview kind="success" style={style.success} text={value(prompt, "successMessage")} /> : null}
          {previewState === "error" ? <FeedbackPreview kind="error" style={style.error} text={value(prompt, "errorMessage")} /> : null}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button data-preview-passive="true" tabIndex={-1} size="small" startIcon={subscribed ? <NotificationsOffRoundedIcon /> : <NotificationsActiveRoundedIcon />} disabled={busy} sx={{ pointerEvents: "none", ...actionSx(mainAction, style.processing, busy) }}>
              {busy ? value(prompt, "processingLabel") : subscribed ? value(prompt, "disableLabel") : value(prompt, "enableLabel")}
            </Button>
            <Button data-preview-passive="true" tabIndex={-1} size="small" startIcon={<CloseRoundedIcon />} disabled={busy} sx={{ pointerEvents: "none", ...actionSx(style.dismissButton, style.processing, busy) }}>
              {value(prompt, "dismissLabel")}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function WebPushPromptPreview({ form, previewState }) {
  const prompt = promptValue(form);
  const businessName = form?.business?.name || "Mi negocio";
  const alignItems = value(prompt, "position") === "bottom-left" ? "flex-start" : "flex-end";
  return (
    <Box sx={{ minHeight: 700, bgcolor: form?.theme?.backgroundColor || "#EEF0F5", p: { xs: 1.5, md: 3 }, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems }}>
      {prompt.enabled === false ? (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, maxWidth: 460 }}>
          <Typography sx={{ fontWeight: 900 }}>Prompt oculto</Typography>
          <Typography variant="body2" sx={{ mt: 0.6, color: "text.secondary" }}>Web Push puede seguir activo técnicamente; este ajuste sólo evita mostrar la invitación pública.</Typography>
        </Paper>
      ) : <PromptCard prompt={prompt} businessName={businessName} previewState={previewState} />}
    </Box>
  );
}

export default function WebPushPromptEditor({ form, device, saving = false, onCommit }) {
  const prompt = promptValue(form);
  const [previewState, setPreviewState] = React.useState("initial");
  const set = React.useCallback((key, nextValue) => onCommit?.(`notifications.webPushPrompt.${key}`, nextValue), [onCommit]);
  return (
    <PageEditorShell title="Notificaciones" description="Configurá copy, posición y apariencia del prompt Web Push sin modificar permisos, suscripciones ni endpoints." device={device} saving={saving} preview={<WebPushPromptPreview form={form} previewState={previewState} />}>
      <EditorGroup title="Vista previa" description="Sólo cambia la representación del editor; no solicita permisos ni ejecuta Web Push.">
        <SegmentedChoiceSetting label="Estado" value={previewState} onChange={setPreviewState} options={PREVIEW_STATES} />
      </EditorGroup>
      <EditorGroup title="Visibilidad">
        <ToggleSetting label="Mostrar invitación Web Push" checked={prompt.enabled} onChange={(next) => set("enabled", next)} />
        <SelectSetting label="Posición" value={value(prompt, "position")} onChange={(next) => set("position", next)} options={[{ value: "bottom-right", label: "Abajo a la derecha" }, { value: "bottom-left", label: "Abajo a la izquierda" }]} />
      </EditorGroup>
      <EditorGroup title="Estado inicial · copy">
        <TextSetting label="Título" value={value(prompt, "title")} onChange={(next) => set("title", next)} />
        <TextSetting label="Descripción" value={value(prompt, "description")} onChange={(next) => set("description", next)} multiline helperText="Podés usar {businessName}." />
        <TextSetting label="Botón activar" value={value(prompt, "enableLabel")} onChange={(next) => set("enableLabel", next)} />
      </EditorGroup>
      <EditorGroup title="Estado suscripto · copy">
        <TextSetting label="Título" value={value(prompt, "subscribedTitle")} onChange={(next) => set("subscribedTitle", next)} />
        <TextSetting label="Descripción" value={value(prompt, "subscribedDescription")} onChange={(next) => set("subscribedDescription", next)} multiline helperText="Podés usar {businessName}." />
        <TextSetting label="Botón desactivar" value={value(prompt, "disableLabel")} onChange={(next) => set("disableLabel", next)} />
      </EditorGroup>
      <EditorGroup title="Acciones y mensajes · copy">
        <TextSetting label="Ahora no" value={value(prompt, "dismissLabel")} onChange={(next) => set("dismissLabel", next)} />
        <TextSetting label="Procesando" value={value(prompt, "processingLabel")} onChange={(next) => set("processingLabel", next)} />
        <TextSetting label="Éxito al activar" value={value(prompt, "successMessage")} onChange={(next) => set("successMessage", next)} />
        <TextSetting label="Éxito al desactivar" value={value(prompt, "disableSuccessMessage")} onChange={(next) => set("disableSuccessMessage", next)} />
        <TextSetting label="Error al activar" value={value(prompt, "errorMessage")} onChange={(next) => set("errorMessage", next)} />
        <TextSetting label="Error al desactivar" value={value(prompt, "disableErrorMessage")} onChange={(next) => set("disableErrorMessage", next)} />
      </EditorGroup>
      <WebPushPromptStyleEditor style={prompt.style || WEB_PUSH_PROMPT_STYLE_DEFAULTS} onCommit={onCommit} />
    </PageEditorShell>
  );
}
